import { MapBorderLineType, MapBorderType } from "../enum/map";
import { getRules } from "./getrules";
import type { Rules } from "./rule";
import { jsonFromPrismaBytes } from "../server/util/prisma";

export type ApiMapCreator = {
    id: number;
    name: string;
    avatar?: string;
}

export type ApiMapCountry = {
    id: number;
    name: string;
    x: number;
    y: number;
    maxUnits?: number;
    startNeutral?: boolean;
    attackDieFacesDiff?: number;
    defendDieFacesDiff?: number;
    attackBonus?: number;
    defendBonus?: number;
    domId?: string;
};

export type ApiMapContinent = {
    countries: number[];
    id: number;
    name: string;
    bonus: number;
};

interface ApiMapBorderBase {
    from: number;
    to: number;
    type?: MapBorderType;
    lineType?: MapBorderLineType;
};

interface ApiMapBorderStraight extends ApiMapBorderBase {
    lineType?: MapBorderLineType.Straight;
};

interface ApiMapBorderCurved extends ApiMapBorderBase {
    lineType: MapBorderLineType.Quadratic;
    cx?: number;
    cy?: number;
}

interface ApiMapBorderDomId extends ApiMapBorderBase {
    lineType: MapBorderLineType.DomId;
    domId?: string;
}

export type ApiMapBorder = ApiMapBorderStraight | ApiMapBorderCurved | ApiMapBorderDomId;

export enum ApiMapCardAssetType {
    Default = 0,
    Custom,
}

export type ApiMapCard = {
    id: number;
    name: string;
    count: number;
    isWild?: boolean;
    assetType?: ApiMapCardAssetType;
};

export const API_MAP_CARD_ASSET_MAX_SIZE_BYTES = 25000;
export const API_MAP_CARD_ASSET_WIDTH = 75;
export const API_MAP_CARD_ASSET_HEIGHT = 100;

export type ApiMapData = {
    backgroundColor?: string;
    backgroundImage?: string;
    countries: ApiMapCountry[];
    continents: ApiMapContinent[];
    borders: ApiMapBorder[];
    cards: ApiMapCard[];
    rules: Rules;
    width: number;
    height: number;
    version?: string;
    textSize?: number;
    mapColor?: string;
};

export type ApiMap = {
    id: number;
    name: string;
    shortName: string;
    asset: string;
    creatorId: number;
    creator?: ApiMapCreator;
    data: ApiMapData;
    cardPrefix?: string;
    published: number;
};

function constructApiMapData(obj: any): ApiMapData {
    const data: ApiMapData = {
        backgroundColor: null,
        backgroundImage: null,
        countries: [],
        continents: [],
        borders: [],
        cards: [],
        rules: null,
        width: 0,
        height: 0,
        version: null,
        textSize: null,
        mapColor: null,
    };

    if (obj == null) {
        return data;
    }

    for (const key of ['backgroundColor', 'backgroundImage', 'version', 'mapColor']) {
        if (Object.hasOwn(obj, key) && obj[key] != null) {
            data[key] = String(obj[key]);
        }
    }
    for (const key of ['width', 'height', 'textSize']) {
        if (Object.hasOwn(obj, key) && obj[key] != null) {
            data[key] = Number(obj[key]);
        }
    }

    if (Object.hasOwn(obj, 'countries') && Array.isArray(obj.countries)) {
        for (const parsedCountry of obj.countries) {
            const country: ApiMapCountry = {
                id: Object.hasOwn(parsedCountry, 'id') && parsedCountry.id != null ?
                    Number(parsedCountry.id) : null,
                name: Object.hasOwn(parsedCountry, 'name') && parsedCountry.name != null ?
                    String(parsedCountry.name) : null,
                x: Object.hasOwn(parsedCountry, 'x') && parsedCountry.x != null ?
                    Number(parsedCountry.x) : 0,
                y: Object.hasOwn(parsedCountry, 'y') && parsedCountry.y != null ?
                    Number(parsedCountry.y) : 0,
                startNeutral: !!parsedCountry.startNeutral,
                attackDieFacesDiff: Object.hasOwn(parsedCountry, 'attackDieFacesDiff') && parsedCountry.attackDieFacesDiff != null ?
                    Number(parsedCountry.attackDieFacesDiff) : 0,
                defendDieFacesDiff: Object.hasOwn(parsedCountry, 'defendDieFacesDiff') && parsedCountry.defendDieFacesDiff != null ?
                    Number(parsedCountry.defendDieFacesDiff) : 0,
                attackBonus: Object.hasOwn(parsedCountry, 'attackBonus') && parsedCountry.attackBonus != null ?
                    Number(parsedCountry.attackBonus) : 0,
                defendBonus: Object.hasOwn(parsedCountry, 'defendBonus') && parsedCountry.defendBonus != null ?
                    Number(parsedCountry.defendBonus) : 0,
                maxUnits: Object.hasOwn(parsedCountry, 'maxUnits') && parsedCountry.maxUnits != null ?
                    Number(parsedCountry.maxUnits) : 0,
                domId: Object.hasOwn(parsedCountry, 'domId') && parsedCountry.domId != null ?
                    String(parsedCountry.domId) :
                    null,
            };
            data.countries.push(country);
        }
    }
    if (Object.hasOwn(obj, 'continents') && Array.isArray(obj.continents)) {
        for (const parsedContinent of obj.continents) {
            const continent: ApiMapContinent = {
                countries: [],
                id: Object.hasOwn(parsedContinent, 'id') && parsedContinent.id != null ?
                    Number(parsedContinent.id) : null,
                name: Object.hasOwn(parsedContinent, 'name') && parsedContinent.name != null ?
                    String(parsedContinent.name) : null,
                bonus: Object.hasOwn(parsedContinent, 'bonus') && parsedContinent.bonus != null ?
                    Number(parsedContinent.bonus) : 0,
            }
            if (Object.hasOwn(parsedContinent, 'countries') && Array.isArray(parsedContinent.countries)) {
                continent.countries = parsedContinent.countries.map((countryId: string | number) => Number(countryId));
            }
            data.continents.push(continent);
        }
    }
    if (Object.hasOwn(obj, 'borders') && Array.isArray(obj.borders)) {
        for (const parsedBorder of obj.borders) {
            const border: ApiMapBorder = {
                from: Object.hasOwn(parsedBorder, 'from') && parsedBorder.from != null ?
                    Number(parsedBorder.from) : null,
                to: Object.hasOwn(parsedBorder, 'to') && parsedBorder.to != null ?
                    Number(parsedBorder.to) : null,
                type: Object.hasOwn(parsedBorder, 'type') && parsedBorder.type != null ?
                    Number(parsedBorder.type) : MapBorderType.TWO_WAY,
                lineType: Object.hasOwn(parsedBorder, 'lineType') && parsedBorder.type != null ?
                    Number(parsedBorder.lineType) : MapBorderLineType.Straight,
            }

            if (Object.hasOwn(parsedBorder, 'cx') && parsedBorder.cx != null) {
                (border as any).cx = Number(parsedBorder.cx);
            }

            if (Object.hasOwn(parsedBorder, 'cy') && parsedBorder.cy != null) {
                (border as any).cy = Number(parsedBorder.cy);
            }

            if (Object.hasOwn(parsedBorder, 'domId') && parsedBorder.domId != null) {
                (border as any).domId = String(parsedBorder.domId);
            }

            data.borders.push(border);
        }
    }
    if (Object.hasOwn(obj, 'cards') && Array.isArray(obj.cards)) {
        for (const parsedCard of obj.cards) {
            const card: ApiMapCard = {
                id: Object.hasOwn(parsedCard, 'id') && parsedCard.id != null ?
                    Number(parsedCard.id) : null,
                name: Object.hasOwn(parsedCard, 'name') && parsedCard.name != null ?
                    String(parsedCard.name) : null,
                count: Object.hasOwn(parsedCard, 'count') && parsedCard.count != null ?
                    Number(parsedCard.count) : 0,
                isWild: !!parsedCard.isWild,
                assetType: Object.hasOwn(parsedCard, 'assetType') && parsedCard.assetType != null ?
                    Number(parsedCard.assetType) : ApiMapCardAssetType.Default,
            };
            data.cards.push(card);
        }
    }

    if (Object.hasOwn(obj, 'rules')) {
        data.rules = getRules(obj.rules);
    }

    return data;
}

function constructApiMapUser(obj?: any): ApiMapCreator {
    if (obj == null || typeof obj !== 'object') {
        return null;
    }

    const user: ApiMapCreator = {
        id: Object.hasOwn(obj, 'id') && obj.id != null ?
            Number(obj.id) : null,
        name: Object.hasOwn(obj, 'name') && obj.name != null ?
            String(obj.name) : null,
        avatar: Object.hasOwn(obj, 'avatar') && obj.avatar != null ?
            String(obj.avatar) : null,
    };

    return user;
}

export function constructApiMap(obj: any): ApiMap {
    const map: ApiMap = {
        id: null,
        name: null,
        shortName: null,
        asset: null,
        creatorId: null,
        data: null,
        cardPrefix: null,
        published: null,
        creator: null,
    };

    if (obj == null) {
        map.data = constructApiMapData(null);
        return map;
    }

    for (const key of ['id', 'creatorId']) {
        if (Object.hasOwn(obj, key) && obj[key] != null) {
            map[key] = Number(obj[key]);
        }
    }
    for (const key of ['name', 'shortName', 'asset', 'cardPrefix']) {
        if (Object.hasOwn(obj, key) && obj[key] != null) {
            map[key] = String(obj[key]);
        }
    }

    map.data = constructApiMapData(
        jsonFromPrismaBytes(obj.data),
    );

    map.creator = constructApiMapUser(
        obj.creator,
    );

    if (obj.published instanceof Date) {
        map.published = obj.published.getTime();
    } else if ('number' === typeof obj.published) {
        map.published = obj.published;
    } // else default of null above

    return map;
}
